import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';
import DataTable from 'react-data-table-component';
import { 
  Euro, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Trash2, 
  History, 
  Activity,
  Search,
  Plus,
  Lock,
  Star,
  AlertTriangle,
  FileText,
  Clock,
  RefreshCw
} from 'lucide-react';
import { getApiBaseUrl, getAppBaseUrl } from '../utils/baseUrl';
import { useLoading, acquireTokenSafe } from '../App';

const apiUrl = getApiBaseUrl();

interface DebtItem {
  xeroInvoiceId: string;
  invoiceNumber: string;
  clientName: string;
  isGoldPartner: boolean;
  amountDue: number;
  total: number;
  invoiceStatus: string;
  invoiceDate: string;
  invoiceDueDate: string;
  debtAgeDays: number;
  category: string;
  chaseCount: number;
  notes: string;
  lastUpdated: string | null;
}

interface KPIs {
  totalDebt: number;
  invoiceCount: number;
  avgDebtAgeDays: number;
  categoryStats: Record<string, { count: number; value: number }>;
  previousSnapshot: {
    totalDebt: number;
    invoiceCount: number;
    avgDebtAgeDays: number;
    snapshotDate: string;
  } | null;
}

interface HistoryItem {
  Id: number;
  XeroInvoiceId: string;
  InvoiceNumber: string;
  ClientName: string;
  FinalAmount: number;
  FinalDebtAgeDays: number;
  Category: string;
  ChaseCount: number;
  RemovalType: string;
  RemovalReason: string;
  RemovalNotes: string;
  RemovedAt: string;
  RemovedBy: string;
}

interface ActivityItem {
  Id: number;
  XeroInvoiceId: string;
  InvoiceNumber: string;
  ClientName: string;
  Action: string;
  Details: string;
  PerformedBy: string;
  PerformedAt: string;
}

const DebtTracking = () => {
  const { instance } = useMsal();
  const { setLoading } = useLoading();
  const [items, setItems] = useState<DebtItem[]>([]);
  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [activeTab, setActiveTab] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');

  // Dialog states
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DebtItem | null>(null);
  const [editNotes, setEditNotes] = useState('');
  
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [removeNotes, setRemoveNotes] = useState('');

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [historySearch, setHistorySearch] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);

  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const getAuthHeader = async () => {
    const accounts = instance.getAllAccounts();
    const token = await acquireTokenSafe(instance, accounts);
    if (!token) throw new Error('No account found');
    return `Bearer ${token}`;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/debt-tracking`, {
        headers: { Authorization: authHeader },
      });

      if (response.status === 403) {
        setHasAccess(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setHasAccess(true);
      const data = await response.json();
      setItems(data.items);
      setKpis(data.kpis);
      setCategories(data.categories);
    } catch (err) {
      console.error("Error fetching data:", err);
      setSnackbarMessage("Failed to fetch debt tracking data");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivity = async () => {
    setActivityLoading(true);
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/debt-tracking/activity?limit=50`, {
        headers: { Authorization: authHeader },
      });

      if (!response.ok) throw new Error('Failed to fetch activity');

      const data = await response.json();
      setActivityItems(data);
    } catch (err) {
      console.error("Error fetching activity:", err);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchHistory = async (search?: string) => {
    setHistoryLoading(true);
    try {
      const authHeader = await getAuthHeader();
      const url = search 
        ? `${apiUrl}/api/debt-tracking/history?search=${encodeURIComponent(search)}`
        : `${apiUrl}/api/debt-tracking/history`;
      const response = await fetch(url, {
        headers: { Authorization: authHeader },
      });

      if (!response.ok) throw new Error('Failed to fetch history');

      const data = await response.json();
      setHistoryItems(data);
    } catch (err) {
      console.error("Error fetching history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const updateCategory = async (item: DebtItem, newCategory: string) => {
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/debt-tracking/${item.xeroInvoiceId}`, {
        method: 'PUT',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ category: newCategory }),
      });

      if (!response.ok) throw new Error('Failed to update category');

      setItems(prev => prev.map(i => 
        i.xeroInvoiceId === item.xeroInvoiceId ? { ...i, category: newCategory } : i
      ));

      setSnackbarMessage('Category updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchActivity();
    } catch (err) {
      console.error("Error updating category:", err);
      setSnackbarMessage("Failed to update category");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const incrementChaseCount = async (item: DebtItem) => {
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/debt-tracking/${item.xeroInvoiceId}`, {
        method: 'PUT',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ chaseCount: item.chaseCount + 1 }),
      });

      if (!response.ok) throw new Error('Failed to update chase count');

      setItems(prev => prev.map(i => 
        i.xeroInvoiceId === item.xeroInvoiceId ? { ...i, chaseCount: i.chaseCount + 1 } : i
      ));

      setSnackbarMessage('Chase count updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchActivity();
    } catch (err) {
      console.error("Error updating chase count:", err);
      setSnackbarMessage("Failed to update chase count");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const saveNotes = async () => {
    if (!selectedItem) return;

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/debt-tracking/${selectedItem.xeroInvoiceId}/notes`, {
        method: 'PUT',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: editNotes }),
      });

      if (!response.ok) throw new Error('Failed to save notes');

      setItems(prev => prev.map(i => 
        i.xeroInvoiceId === selectedItem.xeroInvoiceId ? { ...i, notes: editNotes } : i
      ));

      setNotesDialogOpen(false);
      setSnackbarMessage('Notes saved');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchActivity();
    } catch (err) {
      console.error("Error saving notes:", err);
      setSnackbarMessage("Failed to save notes");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const removeItem = async () => {
    if (!selectedItem || !removeReason) return;

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/debt-tracking/${selectedItem.xeroInvoiceId}`, {
        method: 'DELETE',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: removeReason, notes: removeNotes }),
      });

      if (!response.ok) throw new Error('Failed to remove item');

      setItems(prev => prev.filter(i => i.xeroInvoiceId !== selectedItem.xeroInvoiceId));
      setRemoveDialogOpen(false);
      setRemoveReason('');
      setRemoveNotes('');
      setSnackbarMessage('Invoice removed from tracking');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchActivity();
      fetchData(); // Refresh KPIs
    } catch (err) {
      console.error("Error removing item:", err);
      setSnackbarMessage("Failed to remove item");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const getTrendIcon = (current: number, previous: number, lowerIsBetter: boolean = false) => {
    const diff = current - previous;
    const percentChange = previous !== 0 ? ((diff / previous) * 100) : 0;
    
    if (Math.abs(percentChange) < 1) {
      return <Minus size={14} style={{ color: 'var(--text-secondary)' }} />;
    }
    
    const isGood = lowerIsBetter ? diff < 0 : diff > 0;
    const color = isGood ? 'var(--accent-green)' : 'var(--accent-red)';
    
    if (diff > 0) {
      return (
        <span className="trend-indicator" style={{ color }}>
          <TrendingUp size={14} />
          <span className="trend-value">+{percentChange.toFixed(1)}%</span>
        </span>
      );
    } else {
      return (
        <span className="trend-indicator" style={{ color }}>
          <TrendingDown size={14} />
          <span className="trend-value">{percentChange.toFixed(1)}%</span>
        </span>
      );
    }
  };

  const getDebtAgeColor = (days: number) => {
    if (days <= 30) return 'var(--status-success)';
    if (days <= 60) return 'var(--status-warning)';
    if (days <= 90) return 'var(--accent-orange)';
    return 'var(--status-error)';
  };

  const getDebtAgeClass = (days: number) => {
    if (days <= 30) return 'status-healthy';
    if (days <= 60) return 'status-moderate';
    if (days <= 90) return 'status-at-risk';
    return 'status-critical';
  };

  const filteredItems = items.filter(item => {
    const matchesText = !filterText || 
      item.clientName.toLowerCase().includes(filterText.toLowerCase()) ||
      item.invoiceNumber.toLowerCase().includes(filterText.toLowerCase());
    const matchesCategory = filterCategory === 'All' || item.category === filterCategory;
    return matchesText && matchesCategory;
  });

  // Inline notes update handler
  const handleInlineNotesChange = (item: DebtItem, newNotes: string) => {
    setItems(prev => prev.map(i => 
      i.xeroInvoiceId === item.xeroInvoiceId ? { ...i, notes: newNotes } : i
    ));
  };

  const handleInlineNotesBlur = async (item: DebtItem, newNotes: string) => {
    // Only save if notes actually changed
    const originalItem = items.find(i => i.xeroInvoiceId === item.xeroInvoiceId);
    if (originalItem && originalItem.notes === newNotes) return;
    
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/debt-tracking/${item.xeroInvoiceId}/notes`, {
        method: 'PUT',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: newNotes }),
      });

      if (!response.ok) throw new Error('Failed to save notes');
      fetchActivity();
    } catch (err) {
      console.error("Error saving notes:", err);
      setSnackbarMessage("Failed to save notes");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const columns = [
    {
      name: 'Client',
      selector: (row: DebtItem) => row.clientName,
      sortable: true,
      cell: (row: DebtItem) => (
        <div className="cell-with-icon">
          {row.isGoldPartner && (
            <span title="Gold Partner">
              <Star size={14} style={{ color: 'var(--accent-yellow)' }} />
            </span>
          )}
          <span>{row.clientName}</span>
        </div>
      ),
    },
    {
      name: 'Invoice',
      selector: (row: DebtItem) => row.invoiceNumber,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Amount Due',
      selector: (row: DebtItem) => row.amountDue,
      sortable: true,
      width: '120px',
      cell: (row: DebtItem) => (
        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
          {formatCurrency(row.amountDue)}
        </span>
      ),
    },
    {
      name: 'Debt Age',
      selector: (row: DebtItem) => row.debtAgeDays,
      sortable: true,
      width: '100px',
      cell: (row: DebtItem) => (
        <span className={`status-badge ${getDebtAgeClass(row.debtAgeDays)}`}>
          {row.debtAgeDays} days
        </span>
      ),
    },
    {
      name: 'Category',
      width: '170px',
      cell: (row: DebtItem) => (
        <select
          className="inline-select"
          value={row.category}
          onChange={(e) => updateCategory(row, e.target.value)}
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      ),
    },
    {
      name: 'Chases',
      width: '90px',
      cell: (row: DebtItem) => (
        <div className="chase-cell">
          <span className="chase-count">{row.chaseCount}</span>
          <button
            className="icon-button"
            onClick={() => incrementChaseCount(row)}
            title="Add chase"
          >
            <Plus size={14} />
          </button>
        </div>
      ),
    },
    {
      name: 'Notes',
      width: '220px',
      cell: (row: DebtItem) => (
        <input
          type="text"
          className="inline-notes-input"
          value={row.notes || ''}
          placeholder="Add notes..."
          onChange={(e) => handleInlineNotesChange(row, e.target.value)}
          onBlur={(e) => handleInlineNotesBlur(row, e.target.value)}
        />
      ),
    },
    {
      name: '',
      width: '50px',
      cell: (row: DebtItem) => (
        <button
          className="icon-button danger"
          onClick={() => {
            setSelectedItem(row);
            setRemoveDialogOpen(true);
          }}
          title="Remove from tracking"
        >
          <Trash2 size={16} />
        </button>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: 'var(--bg-secondary)' } },
    headRow: { style: { backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-primary)' } },
    headCells: { style: { color: 'var(--text-secondary)', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' as const, padding: '10px 12px' } },
    rows: {
      style: { backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-primary)', minHeight: '48px' },
    },
    cells: { style: { padding: '8px 12px' } },
    pagination: { style: { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderTop: '1px solid var(--border-primary)' } },
  };

  useEffect(() => {
    fetchData();
    fetchActivity();
  }, [instance]);

  // Access denied view
  if (hasAccess === false) {
    return (
      <div className="page-container">
        <div className="page-header compact">
          <div className="page-title">
            <Euro size={20} />
            <h1>Debt Tracking</h1>
          </div>
        </div>
        <div className="warning-banner">
          <AlertTriangle size={20} />
          <span>You don't have access to debt tracking. Please contact an administrator if you believe you should have access.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header compact">
        <div className="page-title">
          <Euro size={20} />
          <h1>Debt Tracking</h1>
        </div>
      </div>

      {/* KPI Cards */}
      {kpis && (
        <div className="stats-grid stats-grid-4">
          <div className="stat-card">
            <div className="stat-icon" style={{ color: 'var(--accent-red)' }}>
              <Euro size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: 'var(--accent-red)' }}>
                {formatCurrency(kpis.totalDebt)}
                {kpis.previousSnapshot && getTrendIcon(kpis.totalDebt, kpis.previousSnapshot.totalDebt, true)}
              </div>
              <div className="stat-label">Total Outstanding</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ color: 'var(--accent-blue)' }}>
              <FileText size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: 'var(--accent-blue)' }}>
                {kpis.invoiceCount}
                {kpis.previousSnapshot && getTrendIcon(kpis.invoiceCount, kpis.previousSnapshot.invoiceCount, true)}
              </div>
              <div className="stat-label">Invoice Count</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ color: getDebtAgeColor(kpis.avgDebtAgeDays) }}>
              <Clock size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: getDebtAgeColor(kpis.avgDebtAgeDays) }}>
                {kpis.avgDebtAgeDays} days
                {kpis.previousSnapshot && getTrendIcon(kpis.avgDebtAgeDays, kpis.previousSnapshot.avgDebtAgeDays, true)}
              </div>
              <div className="stat-label">Avg Debt Age</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ color: 'var(--accent-yellow)' }}>
              <AlertTriangle size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: 'var(--accent-yellow)' }}>
                {kpis.categoryStats['Uncategorised']?.count || 0}
              </div>
              <div className="stat-label">Uncategorised</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab-button ${activeTab === 0 ? 'active' : ''}`}
          onClick={() => setActiveTab(0)}
        >
          Active Invoices
        </button>
        <button 
          className={`tab-button ${activeTab === 1 ? 'active' : ''}`}
          onClick={() => setActiveTab(1)}
        >
          <Activity size={16} /> Activity Stream
        </button>
      </div>

      {activeTab === 0 && (
        <>
          {/* Filters */}
          <div className="filters-row">
            <div className="search-container">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search client or invoice..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>
            <select
              className="filter-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="All">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <div className="spacer" />
            <button className="btn btn-secondary" onClick={fetchData}>
              <RefreshCw size={16} /> Refresh
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setHistoryDialogOpen(true);
                fetchHistory();
              }}
            >
              <History size={16} /> History
            </button>
          </div>

          {/* Data Table */}
          <div className="data-table-container">
            <DataTable
              columns={columns}
              data={filteredItems}
              customStyles={customStyles}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              defaultSortFieldId={4}
              defaultSortAsc={false}
              noDataComponent={
                <div className="empty-state">
                  No outstanding invoices found
                </div>
              }
            />
          </div>
        </>
      )}

      {activeTab === 1 && (
        <div className="activity-container">
          {activityLoading ? (
            <div className="loading-state">
              <CircularProgress size={24} />
            </div>
          ) : activityItems.length === 0 ? (
            <div className="empty-state">
              No activity recorded yet
            </div>
          ) : (
            <div className="activity-list">
              {activityItems.map((activity, idx) => (
                <div 
                  key={activity.Id}
                  className="activity-item"
                >
                  <div className="activity-header">
                    <span className="activity-title">
                      {activity.ClientName} - {activity.InvoiceNumber}
                    </span>
                    <span className="activity-time">
                      {new Date(activity.PerformedAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="activity-body">
                    <span className={`activity-action ${activity.Action.includes('Removed') ? 'removed' : 'updated'}`}>
                      {activity.Action}
                    </span>
                    <span className="activity-details">
                      {activity.Details}
                    </span>
                  </div>
                  <div className="activity-footer">
                    by {activity.PerformedBy}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notes Dialog */}
      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#161b22', color: '#c9d1d9', border: '1px solid #30363d' }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #30363d' }}>
          Edit Notes - {selectedItem?.invoiceNumber}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            placeholder="Add notes about this invoice..."
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0d1117',
                color: '#c9d1d9',
                '& fieldset': { borderColor: '#30363d' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #30363d', p: 2 }}>
          <Button onClick={() => setNotesDialogOpen(false)} sx={{ color: '#c9d1d9' }}>
            Cancel
          </Button>
          <Button onClick={saveNotes} variant="contained" sx={{ backgroundColor: '#238636' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#161b22', color: '#c9d1d9', border: '1px solid #30363d' }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #30363d', color: '#f85149' }}>
          Remove Invoice from Tracking
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <p style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 14 }}>
            This will remove <strong>{selectedItem?.invoiceNumber}</strong> from active tracking. 
            It will be archived in history and won't reappear.
          </p>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: '#8b949e' }}>Reason *</InputLabel>
            <Select
              value={removeReason}
              label="Reason *"
              onChange={(e) => setRemoveReason(e.target.value)}
              sx={{ 
                backgroundColor: '#0d1117',
                color: '#c9d1d9',
                '& fieldset': { borderColor: '#30363d' },
              }}
            >
              <MenuItem value="Written Off">Written Off</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
              <MenuItem value="Duplicate">Duplicate</MenuItem>
              <MenuItem value="Client Left">Client Left</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            multiline
            rows={2}
            fullWidth
            value={removeNotes}
            onChange={(e) => setRemoveNotes(e.target.value)}
            placeholder="Additional notes (optional)"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0d1117',
                color: '#c9d1d9',
                '& fieldset': { borderColor: '#30363d' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #30363d', p: 2 }}>
          <Button onClick={() => setRemoveDialogOpen(false)} sx={{ color: '#c9d1d9' }}>
            Cancel
          </Button>
          <Button 
            onClick={removeItem} 
            variant="contained" 
            disabled={!removeReason}
            sx={{ backgroundColor: '#f85149', '&:hover': { backgroundColor: '#da3633' } }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#161b22', color: '#c9d1d9', border: '1px solid #30363d', height: '80vh' }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #30363d' }}>
          Invoice History
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <div className="search-container" style={{ marginBottom: 16 }}>
            <Search size={16} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search history..."
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchHistory(historySearch)}
            />
          </div>
          {historyLoading ? (
            <div className="loading-state">
              <CircularProgress size={24} />
            </div>
          ) : (
            <DataTable
              columns={[
                { name: 'Invoice', selector: (row: HistoryItem) => row.InvoiceNumber, width: '120px' },
                { name: 'Client', selector: (row: HistoryItem) => row.ClientName },
                { name: 'Amount', selector: (row: HistoryItem) => row.FinalAmount, cell: (row: HistoryItem) => formatCurrency(row.FinalAmount), width: '120px' },
                { name: 'Debt Age', selector: (row: HistoryItem) => row.FinalDebtAgeDays, cell: (row: HistoryItem) => `${row.FinalDebtAgeDays} days`, width: '100px' },
                { name: 'Removal Type', selector: (row: HistoryItem) => row.RemovalType, width: '120px',
                  cell: (row: HistoryItem) => (
                    <span className={`status-badge ${row.RemovalType === 'paid' ? 'status-healthy' : 'status-critical'}`}>
                      {row.RemovalType}
                    </span>
                  )
                },
                { name: 'Reason', selector: (row: HistoryItem) => row.RemovalReason || '-', width: '120px' },
                { name: 'Removed', selector: (row: HistoryItem) => row.RemovedAt, cell: (row: HistoryItem) => new Date(row.RemovedAt).toLocaleDateString(), width: '110px' },
                { name: 'By', selector: (row: HistoryItem) => row.RemovedBy, width: '100px' },
              ]}
              data={historyItems}
              customStyles={customStyles}
              pagination
              paginationPerPage={15}
              noDataComponent={
                <div className="empty-state">
                  No history found
                </div>
              }
            />
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #30363d', p: 2 }}>
          <Button onClick={() => setHistoryDialogOpen(false)} sx={{ color: '#c9d1d9' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DebtTracking;
